import { Controller } from "@hotwired/stimulus"
import { autocomplete } from '@algolia/autocomplete-js'
import '@algolia/autocomplete-theme-classic'

export default class extends Controller {
  static targets = ["input", "container", "modal", "overlay"]

  connect() {
    // Add CMD+K and ESC listeners
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.boundHandleKeydown)
    
    // Don't initialize autocomplete until modal is opened
    this.autocompleteInstance = null
  }

  disconnect() {
    // Clean up listeners
    document.removeEventListener('keydown', this.boundHandleKeydown)
    
    // Clean up autocomplete
    if (this.autocompleteInstance) {
      this.autocompleteInstance.destroy()
    }
  }

  // Handle CMD+K and ESC
  handleKeydown(event) {
    // CMD+K or CTRL+K to open
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault()
      this.openModal()
    }
    // ESC to close
    else if (event.key === 'Escape' && this.modalTarget.classList.contains('active')) {
      event.preventDefault()
      this.closeModal()
    }
  }

  openModal() {
    this.modalTarget.classList.add('active')
    this.overlayTarget.classList.add('active')
    document.body.style.overflow = 'hidden'
    
    // Initialize autocomplete on first open
    if (!this.autocompleteInstance) {
      this.initializeAutocomplete()
    }
    
    // Focus the input
    setTimeout(() => {
      const input = this.element.querySelector('.aa-Input')
      if (input) {
        input.focus()
        // Open the autocomplete panel
        if (this.autocompleteInstance) {
          this.autocompleteInstance.setIsOpen(true)
        }
      }
    }, 100)
  }

  closeModal() {
    this.modalTarget.classList.remove('active')
    this.overlayTarget.classList.remove('active')
    document.body.style.overflow = ''
    
    // Clear and close autocomplete
    if (this.autocompleteInstance) {
      this.autocompleteInstance.setQuery('')
      this.autocompleteInstance.setIsOpen(false)
    }
  }

  // Allow clicking overlay to close
  closeOnOverlayClick(event) {
    if (event.target === this.overlayTarget) {
      this.closeModal()
    }
  }

  initializeAutocomplete() {
    const container = this.element.querySelector('#autocomplete-container')
    if (!container) return

    // Get the modal content div to ensure panel renders inside it
    const modalContent = this.element.querySelector('.search-modal-content')

    this.autocompleteInstance = autocomplete({
      container: container,
      panelContainer: modalContent || container,
      placeholder: 'Type to search...',
      openOnFocus: true,
      insights: false,
      debug: false,
      panelPlacement: 'input-wrapper-width',
      detachedMediaQuery: 'none', // Disable Algolia's mobile detached mode
      getSources: async ({ query }) => {
        // Empty prompt source
        if (!query || query.length === 0) {
          return [
            {
              sourceId: 'prompt',
              getItems: () => [],
              templates: {
                noResults: ({ html }) => html`
                  <div class="p-4 text-center text-base-content/70">
                    <div class="font-medium">Start typing to search</div>
                  </div>
                `
              }
            }
          ]
        }

        const items = await this.fetchSearchResults(query)
        
        // Filter out section headers (API returns them mixed with results)
        const actualItems = items.filter((i) => i.type !== 'section')
        
        const groups = {
          clients: actualItems.filter((i) => i.type === 'client'),
          projects: actualItems.filter((i) => i.type === 'project'),
          team: actualItems.filter((i) => i.type === 'team'),
          guides: actualItems.filter((i) => i.type === 'guide'),
          ambassadors: actualItems.filter((i) => i.type === 'ambassador'),
          milestones: actualItems.filter((i) => i.type === 'milestone'),
          tasks: actualItems.filter((i) => i.type === 'task')
        }

        const buildSource = (id, title, icon, data) => ({
          sourceId: `search-${id}`,
          getItems: () => data,
          getItemUrl: ({ item }) => item.url,
          onSelect: ({ item }) => {
            if (item && item.url) {
              window.location.href = item.url
            }
          },
          templates: {
            header({ html }) {
              return html`<div class="px-3 py-2 bg-base-200 border-t border-base-300">
                <div class="flex items-center gap-2">
                  <iconify-icon icon="${icon}" class="text-base-content/70"></iconify-icon>
                  <span class="font-medium text-base-content/70 uppercase tracking-wide text-xs">${title}</span>
                </div>
              </div>`
            },
            item({ item, html }) {
              return html`<div class="flex items-center gap-3 p-3 cursor-pointer">
                <div class="flex-shrink-0">
                  <iconify-icon icon="${item.icon}" class="text-base-content"></iconify-icon>
                </div>
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-base-content truncate text-sm">${item.title}</div>
                  <div class="text-base-content/70 truncate text-xs">${item.subtitle}</div>
                </div>
              </div>`
            }
          }
        })

        const sources = []
        if (groups.clients.length) sources.push(buildSource('clients', 'Clients', 'ph:buildings', groups.clients))
        if (groups.projects.length) sources.push(buildSource('projects', 'Projects', 'ph:folder', groups.projects))
        if (groups.milestones.length) sources.push(buildSource('milestones', 'Milestones', 'ph:flag', groups.milestones))
        if (groups.tasks.length) sources.push(buildSource('tasks', 'Tasks', 'ph:check-square', groups.tasks))
        if (groups.ambassadors.length) sources.push(buildSource('ambassadors', 'Ambassadors', 'ph:users-three', groups.ambassadors))
        if (groups.team.length) sources.push(buildSource('team', 'Team', 'ph:users', groups.team))
        if (groups.guides.length) sources.push(buildSource('guides', 'Guides', 'ph:book', groups.guides))

        if (sources.length === 0) {
          // Single no-results source
          return [
            {
              sourceId: 'no-results',
              getItems: () => [],
              templates: {
                noResults: ({ html }) => html`
                  <div class="p-4 text-center text-base-content/70">
                    <div class="font-medium">No results found</div>
                    <div class="mt-2">Try a different search term</div>
                  </div>
                `
              }
            }
          ]
        }
        return sources
      }
    })
  }

  async fetchSearchResults(query) {
    if (!query || query.length < 1) return []
    
    try {
      const response = await fetch(`/api/search?query=${encodeURIComponent(query)}`)
      const data = await response.json()
      return data.results || []
    } catch (error) {
      console.error('Search error:', error)
      return []
    }
  }
}
